import React, { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { PLACE_TYPE_NAME_CHOISE, PLACE_TYPE_COLOR_CHOISE } from '../../constans';

import StopImgGallery from '../StopImgGallery';
import AddStopButton from '../AddStopButton';
import { BACKEND_URL } from '../../constans';

const ModalWrap = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
    overflowY: 'auto',
    '&:focus': {
        outline: 'none',
    },
    [theme.breakpoints.up('sm')]: {
        maxWidth: '1250px',
        height: '600px',
        borderRadius: '10px',
        overflowY: 'hidden',
    },
}));

const CloseButton = styled(Button)(() => ({
    position: 'absolute',
    top: '28px',
    right: '20px',
    height: '33px',
    width: '68px',
    padding: '8px',
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '5px',
    border: 'none',
    fontFamily: 'Proxima Nova',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.211,
    color: '#1a1a1a',
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.05)',
    },
    '& .MuiButton-startIcon': {
        marginRight: '3px !important',
        marginLeft: '0 !important',
    },
}));

const CloseIconCustom = styled(CloseIcon)(() => ({
    fontSize: '11px !important',
}));

const MobileNameWrap = styled(Box)(({ theme }) => ({
    padding: '10px 23px',
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const PlaceNameMobile = styled(Typography)(({ color }) => ({
    marginTop: '25px',
    marginBottom: '10px',
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: 1.21,
    color: color,
}));

const StopNameMobile = styled(Typography)(() => ({
    marginBottom: '22px',
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: 1.35,
    color: '#1a1a1a',
}));

const FlexWrap = styled(Box)(({ theme }) => ({
    display: 'block',
    [theme.breakpoints.up('sm')]: {
        display: 'flex',
    },
}));

const MainImg = styled(Box)(({ theme, img }) => ({
    width: '100%',
    height: '281px',
    backgroundImage: `url(${img})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '800px',
        height: '600px',
    },
}));

const DescriptionWrap = styled(Box)(({ theme }) => ({
    padding: ' 0px 20px 32px',
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
        padding: '35px 40px 30px',
        maxWidth: '450px',
    },
}));

const PlaceNameDesktop = styled(Typography)(({ theme, color }) => ({
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: 1.21,
    color: color,
    display: 'none',
    [theme.breakpoints.up('sm')]: {
        display: 'block',
    },
}));

const StopNameDesktop = styled(Typography)(({ theme }) => ({
    marginBottom: '22px',
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: 1.35,
    color: '#1a1a1a',
    display: 'none',
    [theme.breakpoints.up('sm')]: {
        display: 'block',
    },
}));

const AddButtonWrap = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    [theme.breakpoints.up('sm')]: {
        marginTop: 0,
        position: 'absolute',
        bottom: '30px',
    },
}));

const FullDescriptions = styled(Box)(() => ({
    fontFamily: 'Proxima Nova',
    lineHeight: '24px',
}));

const StopInfoModal = ({ stop, open, isFromAddedStations, handleClose, handleClick }) => {
    const [currentImg, setCurrentImg] = useState(null);
    const [currentIndx, setCurrentIndx] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    useEffect(() => {
        if (stop?.images) {
            setCurrentImg(stop.images[0]);
        }
    }, [stop]);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

    const onTouchEnd = useCallback(
        (indx) => {
            if (!touchStart || !touchEnd) return;
            const distance = touchStart - touchEnd;
            const isLeftSwipe = distance > 0;
            const isRightSwipe = distance < 0;
            if (isLeftSwipe) {
                if (stop.images.length - 1 === indx) {
                    return;
                } else {
                    setCurrentIndx(indx + 1);
                    setCurrentImg(stop.images[indx + 1]);
                }
            }
            if (isRightSwipe) {
                if (indx === 0) {
                    return;
                } else {
                    setCurrentIndx(indx - 1);
                    setCurrentImg(stop.images[indx - 1]);
                }
            }
        },
        [stop.images, touchEnd, touchStart]
    );
    const handleChangeImg = (el, indx) => {
        setCurrentIndx(indx);
        setCurrentImg(el);
    };

    const closeModal = () => {
        handleClose();
        setCurrentIndx(0);
        setCurrentImg(stop?.images[0]);
    };

    return (
        <Modal open={open} onClose={closeModal}>
            <ModalWrap>
                <MobileNameWrap>
                    <PlaceNameMobile color={PLACE_TYPE_COLOR_CHOISE[stop.place_type]}>
                        {PLACE_TYPE_NAME_CHOISE[stop.place_type]}
                    </PlaceNameMobile>
                    <StopNameMobile>{stop.name}</StopNameMobile>
                </MobileNameWrap>
                <CloseButton startIcon={<CloseIconCustom />} onClick={closeModal}>
                    Close
                </CloseButton>
                <FlexWrap>
                    <MainImg
                        img={currentImg ? `${BACKEND_URL}${currentImg}` : ''}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={() => onTouchEnd(currentIndx)}
                    >
                        <StopImgGallery
                            imgs={stop.images}
                            desctop={true}
                            currentImg={currentImg}
                            handleChangeImg={setCurrentImg}
                        />
                    </MainImg>
                    <DescriptionWrap>
                        <StopImgGallery
                            imgs={stop.images}
                            desctop={false}
                            currentImg={currentImg}
                            handleChangeImg={handleChangeImg}
                        />
                        <PlaceNameDesktop color={PLACE_TYPE_COLOR_CHOISE[stop.place_type]}>
                            {PLACE_TYPE_NAME_CHOISE[stop.place_type]}
                        </PlaceNameDesktop>
                        <StopNameDesktop>{stop.name}</StopNameDesktop>
                        {stop.description && (
                            <FullDescriptions
                                dangerouslySetInnerHTML={{
                                    __html: stop.description,
                                }}
                            ></FullDescriptions>
                        )}
                        {!isFromAddedStations && (
                            <AddButtonWrap>
                                <AddStopButton handleClick={handleClick} />
                            </AddButtonWrap>
                        )}
                    </DescriptionWrap>
                </FlexWrap>
            </ModalWrap>
        </Modal>
    );
};

export default StopInfoModal;
